import { useEffect, useMemo, useState, ReactNode, useCallback } from 'react'

import { AuthContext } from '@auth/auth.context'
import { useSignOut } from '@auth/auth.hooks'
import { CURRENT_USER_KEY, USER_TOKEN_KEY } from '@auth/auth.localstorage'
import { AuthContextValue } from '@auth/auth.types'
import { useCurrentProfessional } from '@professionals/professionals.hooks'
import { LoggedUser } from '@professionals/professionals.types'
import { useCareProvider } from '@providers/providers.hooks'
import { firebaseAuth } from '@services/firebase'
import { useLocalStorage } from '@services/local-storage.hooks'
import { getCustomTokens, isTokenAboutToExpire } from './auth.service'

type AuthProviderProps = {
  children: ReactNode
}
const AuthProvider = ({ children }: AuthProviderProps) => {
  const { careProvider } = useCareProvider()

  const { setValue: setStorageUser } = useLocalStorage<string | null>(CURRENT_USER_KEY, null)
  const { setValue: storeUserToken, storedValue } = useLocalStorage<string | null>(USER_TOKEN_KEY, null)

  const [currentUser, setCurrentUser] = useState<LoggedUser | null>(null)
  const [userToken, setUserToken] = useState<string | null>(storedValue)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const [isSignOut, setIsSignOut] = useState(false)
  
  const careProviderId = careProvider?.id

  const isAuthorised = !!userToken

  const isLoading = (isAuthenticated === null) || (isAuthenticated && !currentUser)

  const refreshUserToken = useCallback(() => {
    if (!userToken || isTokenAboutToExpire(userToken)) {
      return firebaseAuth?.currentUser?.getIdToken().then(idToken => {
        getCustomTokens(idToken).then(({ niloToken }) => {
          setUserToken(niloToken)
        })
      })
    }
    return null
  }, [setUserToken])

  const signOut = useSignOut({
    onSuccess: () => {
      setIsAuthenticated(false)      
      setUserToken(null)
      setCurrentUser(null)
    },
  })

  const professional = useCurrentProfessional({ enabled: !!userToken, onError: () => signOut.mutate() })

  useEffect(() => {
    setStorageUser(currentUser ? JSON.stringify(currentUser) : null)
  }, [currentUser])

  useEffect(() => {
    storeUserToken(userToken)

    if (!userToken) signOut.mutate()
  }, [userToken])

  useEffect(() => {
    firebaseAuth.onAuthStateChanged(newUser => {      
      setIsAuthenticated(!!newUser)
      if (!newUser) signOut.mutate()
      else refreshUserToken()
    })
  }, [])

  useEffect(() => {
    if (professional && careProviderId) {
      setCurrentUser({ ...professional, careProvider: careProviderId })
    }
  }, [professional, careProviderId])

  useEffect(() => {
    if (!userToken) return undefined

    const handleVisibilityChange = () => refreshUserToken()

    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('visibilitychange', handleVisibilityChange, false)
    }

    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange)
    }

  }, [userToken, refreshUserToken])

  const value: AuthContextValue = useMemo(() => {
    return { isLoading, isAuthorised, isSignOut, currentUser, userToken, setUserToken, setIsSignOut }
  }, [isLoading, isAuthorised, currentUser, userToken, setUserToken, isSignOut])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthProvider
